/* eslint-disable react-hooks/exhaustive-deps */
import React, {useEffect, useRef, useState} from "react"
import {Checkbox, Collapse} from "antd"
import {v4 as uuid} from "uuid"
import {cloneDeep, isBoolean} from "lodash"
import moment from "moment"
import {useTranslation} from "react-i18next"
import {Model} from "Model"
import {BaseInput} from "components"
import {handleError, toastError, toastSuccess, toastWarning} from "helpers"
import {shiftsService} from "services"
import {KlassappTableHOC} from "HOC"
import {ConfirmPopup, KlassappTable, KlassappTableHeader} from "uiKit"
import {BaseButton, SecondaryButton, AddItemCircleButton} from "components/buttons"
import {KlassappTableProps} from "types/common"
import styles from "./Shifts.module.css"
import {BaseTimePicker} from "components/DateTimePicker"
import CampusSelect from "components/CampusSelect"
import ProgramSelect from "components/ProgramSelect"
import {getDisabledEndHours, getDisabledEndMinutes} from "helpers/calendar"
import {INITIAL_WORK_WEEK, WEEKDAY_NAMES, WorkDay} from "types/work-schedule"
import {WorkDayItem} from "sections/CalendarSettings/parts/WorkScheduleDetails/parts"
import ProgramVersionSelect from "components/ProgramVersionSelect"
import {useModel, useVisible} from "hooks"
import {useMutation, useQuery} from "@tanstack/react-query"

const {Panel} = Collapse

type PageProps = {
    t: Function
    model: Model
}

type Props = KlassappTableProps & PageProps

const ShiftTab = (props: Props) => {
    const {
        isShowTableHeaderAction,
        tableHeaderActions,
        page,
        total,
        pageSize,
        columns,
        data,
        fields,
        allFields,
        isHideMenuActions,
        getCurrentData,
        menuActions,
        dispatch,
        onClickShowConfirmModal,
        onUpdateRowData,
        onUpdateTableData,
        onClickSortColumn,
        onDraggableColumn,
        onChangeFields,
        onChangeAllFields,
        onChangePage,
        dispatchFunc,
        onChangeRowPerPage
    } = props

    const {t} = useTranslation(["academics", "common"])
    const model = useModel()
    const popupConfirmManualAssignShift = useVisible()
    const [currentShiftSelected, setCurrentShiftSelected] = useState<{
        shiftId: number
        campusIds: number[]
        majorIds: number[]
        majorVersionIds: number[]
    }>(undefined)
    const [newData, setNewData] = useState<any>(null)
    const currentNewData = useRef<any>()
    const [oldData, setOldData] = useState<any>(null)
    const currentOldData = useRef<any>()

    const settingsQuery = useQuery(
        ["shifts-settings", page, pageSize],
        async () => {
            const {data, total} = await shiftsService.getAll({
                linkedObject: true
            })
            const timeFormat = model.getUserTimeFormat()
            const todayStr = moment().format("YYYY-MM-DD")
            const newData = data.map((item) => {
                const _workWeek = WEEKDAY_NAMES.map((_, index) => {
                    let value = item.workWeek?.find((x) => x.dayOfWeek === index)
                    if (value) {
                        value = {
                            ...value,
                            name: WEEKDAY_NAMES[index],
                            enabled: true,
                            startTime: value.startTime ? moment(todayStr + " " + value.startTime) : undefined,
                            endTime: value.endTime ? moment(todayStr + " " + value.endTime) : undefined,
                            allDay: value.allDay,
                            maxHoursEnabled: !!value.maxHoursEnabled,
                            maxHours: value.maxHours?.toString()
                        }
                    } else {
                        value = {
                            dayOfWeek: index,
                            name: WEEKDAY_NAMES[index],
                            enabled: false,
                            allDay: false,
                            maxHoursEnabled: false,
                            maxHours: undefined
                        }
                    }
                    return value
                })
                item.workWeek = _workWeek
                item.id = item.shiftId
                item.isActiveHtml = renderIsActiveHtml(item.isActive, true)
                item.nameHtml = renderName(item.name, item.workWeek)
                item.startTimeHtml = item.startTime ? moment(todayStr + " " + item.startTime).format(timeFormat) : null
                item.endTimeHtml = item.endTime ? moment(todayStr + " " + item.endTime).format(timeFormat) : null
                item.programsHtml = renderProgramsInput({
                    programs: item.programs,
                    campuses: item.campuses,
                    disabled: true
                })
                item.programVersionsHtml = renderProgramVersionsInput({
                    programs: item.programs,
                    programVersions: item.programVersions,
                    disabled: true
                })
                item.campusesHtml = renderCampusesInput({
                    campuses: item.campuses,
                    programs: item.programs,
                    disabled: true
                })
                item.isAutoAssignShiftHtml = renderIsAutoAssignShift({
                    value: item.isAutoAssignShift,
                    disabled: true
                })
                return item
            })
            return {data: newData, total}
        },
        {
            initialData: {data: [], total: 0},
            onSuccess: (result) => {
                dispatch({data: result.data, total: result.total})
            },
            onError: (err) => {
                handleError(err)
            }
        }
    )

    const createOrEditShiftMutation = useMutation(
        async (payload: {shiftId?: number; isEdit: boolean; data: any}) => {
            if (!payload.isEdit) {
                return shiftsService.create(payload.data)
            }
            return shiftsService.update({...payload.data, shiftId: payload.shiftId})
        },
        {
            onSuccess: () => {
                setNewData(null)
                setOldData(null)
                currentNewData.current = null
                currentOldData.current = null
                settingsQuery.refetch()
            },
            onError: (error) => {
                handleError(error)
            }
        }
    )

    const deleteShiftMutation = useMutation(
        async () => {
            const data = getCurrentData()
            const checkedItems = data.filter((item) => item.isChecked)
            const payload = checkedItems.map((item) => item.shiftId)
            dispatch({isShowTableHeaderAction: false, isHideMenuActions: false})
            return shiftsService.delete({ids: payload})
        },
        {
            onSuccess: () => {
                settingsQuery.refetch()
            },
            onError: (error) => {
                handleError(error)
            }
        }
    )

    const manualAssignShiftMutation = useMutation(
        async (payload: {shiftIds: number[]; campusIds: number[]; majorIds: number[]; majorVersionIds: number[]}) => {
            dispatch({isShowTableHeaderAction: false, isHideMenuActions: false})
            return shiftsService.manualAssign(payload)
        },
        {
            onSuccess: () => {
                settingsQuery.refetch()
                popupConfirmManualAssignShift.close()
                toastSuccess("Assign shift successfully")
            },
            onError: (error) => {
                handleError(error)
            }
        }
    )

    const isLoading =
        !!settingsQuery.isFetching || !!deleteShiftMutation.isLoading || !!createOrEditShiftMutation.isLoading

    const getColumns = () => {
        return [
            {title: t("settings.name"), field: "nameHtml"},
            {title: t("settings.isAutoAssignShift"), field: "isAutoAssignShiftHtml"},
            {title: t("settings.isActive"), field: "isActiveHtml"},
            {title: t("settings.campuses"), field: "campusesHtml", style: {minWidth: "250px"}},
            {title: t("settings.programs"), field: "programsHtml", style: {minWidth: "250px"}},
            {title: t("settings.programVersions"), field: "programVersionsHtml", style: {minWidth: "250px"}}
        ]
    }

    const getPageTitle = () => {
        return t("settings.shifts")
    }
    const getFields = () => {
        return [
            t("settings.name"),
            t("settings.isAutoAssignShift"),
            t("settings.isActive"),
            t("settings.campuses"),
            t("settings.programs"),
            t("settings.programVersions")
        ]
    }

    const getTableHeaderActions = (isShowDuplicateBtn = false, checkedData = []) => {
        const hasLockedItem = (checkedData || []).some((item) => item.isLock)
        const actions = []
        if (!hasLockedItem) {
            actions.push({
                title: t("common:action.delete"),
                icon: "DELETE",
                action: () => onClickShowConfirmModal("DELETE")
            })
        }
        return actions
    }

    useEffect(() => {
        dispatch({isClassComponent: false})
        dispatchFunc([
            {key: "getListData", func: settingsQuery.refetch},
            {key: "getPageTitle", func: getPageTitle},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns},
            {key: "getTableHeaderActions", func: getTableHeaderActions},
            {key: "getMenuActions", func: getMenuActions},
            {key: "onClickDeleteMulti", func: deleteShiftMutation.mutate},
            {key: "onClickEdit", func: onClickEdit},
            {key: "onClickDelete", func: onClickDelete}
        ])
    }, [])

    const renderIsAutoAssignShift = (payload: {value: boolean; disabled?: boolean}) => {
        const {value, disabled} = payload

        if (disabled) {
            return (
                <div className={styles.inputWrap}>
                    <div className={styles.visibleCheckboxWrap}>
                        <Checkbox disabled checked={value} />
                    </div>
                </div>
            )
        }
        return (
            <div className={styles.inputWrap}>
                <div className={styles.visibleCheckboxWrap}>
                    <Checkbox
                        checked={value}
                        onChange={(event) => onChangeNewItemData("isAutoAssignShift", event.target.checked)}
                    />
                </div>
            </div>
        )
    }

    const renderIsActiveHtml = (value, disabled: boolean = false) => {
        if (disabled) {
            return (
                <div className={styles.inputWrap}>
                    <div className={styles.visibleCheckboxWrap}>
                        <Checkbox disabled checked={value} />
                    </div>
                </div>
            )
        }

        return (
            <div className={styles.inputWrap}>
                <div className={styles.visibleCheckboxWrap}>
                    <Checkbox
                        checked={value}
                        onChange={(event) => onChangeNewItemData("isActive", event.target.checked)}
                    />
                </div>
            </div>
        )
    }

    const getMenuActions = () => {
        return [
            {
                title: t("common:action.edit"),
                icon: "EDIT",
                action: onClickEdit,
                canShow: () => {
                    return true
                }
            },
            {
                title: "Manual Assign Shift ",
                icon: "EDIT",
                action: onManualAssignShift,
                canShow: () => {
                    return true
                }
            },
            {
                title: t("common:action.delete"),
                icon: "DELETE",
                action: onClickDelete,
                canShow: () => {
                    return true
                }
            }
        ]
    }

    const onManualAssignShift = (editItem) => {
        popupConfirmManualAssignShift.open()
        const majorIds = editItem.programs?.map((p) => p.id) || []
        const campusIds = editItem.campuses?.map((c) => c.id) || []
        const majorVersionIds = editItem.programVersions?.map((p) => p.id) || []

        setCurrentShiftSelected({
            shiftId: editItem.shiftId,
            campusIds,
            majorIds,
            majorVersionIds
        })
    }

    const onClickEdit = (editItem) => {
        const newItemData = cloneDeep(currentNewData.current)
        if (newItemData) {
            toastWarning(t("common:validation.saveBeforeEdit"))
            return
        }
        setNewData(
            cloneDeep({
                ...editItem,
                nameData: editItem.name,
                startTime: editItem.startTime,
                endTime: editItem.endTime,
                isActive: !!editItem.isActive,
                isAutoAssignShift: !!editItem.isAutoAssignShift
            })
        )
        setOldData(cloneDeep(editItem))
        currentNewData.current = cloneDeep({
            ...editItem,
            nameData: editItem.name,
            startTime: editItem.startTime,
            endTime: editItem.endTime,
            isActive: !!editItem.isActive,
            isAutoAssignShift: !!editItem.isAutoAssignShift
        })
        currentOldData.current = cloneDeep(editItem)
        const data = getCurrentData()
        const newItem = data.map((item) => {
            if (item.shiftId === editItem.shiftId) {
                item.isActiveHtml = renderIsActiveHtml(editItem.isActive)
                item.nameHtml = renderNameInput({value: editItem.name, workWeek: editItem.workWeek})
                item.isAutoAssignShiftHtml = renderIsAutoAssignShift({value: editItem.isAutoAssignShift})
                item.startTimeHtml = renderTimeInput({
                    key: "startTime",
                    startTime: editItem.startTime,
                    endTime: editItem.endTime
                })
                item.endTimeHtml = renderTimeInput({
                    key: "endTime",
                    startTime: editItem.startTime,
                    endTime: editItem.endTime
                })
                item.programsHtml = renderProgramsInput({programs: editItem.programs, campuses: editItem.campuses})
                item.campusesHtml = renderCampusesInput({campuses: editItem.campuses, programs: editItem.programs})
                item.programVersionsHtml = renderProgramVersionsInput({
                    programVersions: editItem.programVersions,
                    programs: editItem.programs
                })
                return item
            }
            return item
        })
        dispatch({data: newItem})
    }

    const onClickDelete = async (deletedItem) => {
        if (deletedItem.isForm) {
            setNewData(null)
            setOldData(null)
            currentNewData.current = null
            currentOldData.current = null
            settingsQuery.refetch()
            return
        }
        try {
            dispatch({isLoading: true})
            setNewData(null)
            setOldData(null)
            currentNewData.current = null
            currentOldData.current = null
            await shiftsService.delete({ids: [deletedItem.shiftId]})
            settingsQuery.refetch()
        } catch (e) {
            handleError(e)
        } finally {
            dispatch({isLoading: false})
        }
    }

    const onChangeNewItemData = (key: string, value: any) => {
        currentNewData.current[key] = value
        setNewData(cloneDeep(currentNewData.current))
        const data = getCurrentData()
        const newItem = data.map((item) => {
            if (item.id === currentNewData.current.id) {
                item.nameHtml = renderNameInput({
                    value: currentNewData.current.name,
                    workWeek: currentNewData.current.workWeek
                })
                item.startTimeHtml = renderTimeInput({
                    key: "startTime",
                    startTime: currentNewData.current.startTime,
                    endTime: currentNewData.current.endTime
                })
                item.endTimeHtml = renderTimeInput({
                    key: "endTime",
                    endTime: currentNewData.current.endTime,
                    startTime: currentNewData.current.startTime
                })
                item.isActiveHtml = renderIsActiveHtml(currentNewData.current.isActive)
                item.isAutoAssignShiftHtml = renderIsAutoAssignShift({value: currentNewData.current.isAutoAssignShift})
                item.programsHtml = renderProgramsInput({
                    programs: currentNewData.current.programs,
                    campuses: currentNewData.current.campuses
                })
                item.programVersionsHtml = renderProgramVersionsInput({
                    programVersions: currentNewData.current.programVersions,
                    programs: currentNewData.current.programs
                })
                item.campusesHtml = renderCampusesInput({
                    campuses: currentNewData.current.campuses,
                    programs: currentNewData.current.programs
                })
                return item
            }
            return item
        })
        dispatch({data: cloneDeep(newItem)})
    }

    const renderName = (name: string, workWeek: WorkDay[]) => {
        return (
            <div className={styles.inputWrap}>
                <div className={styles.name}>{name}</div>
                {renderWorkWeek({disabled: true, workWeek})}
            </div>
        )
    }

    const renderWorkWeek = (payload: {disabled?: boolean; workWeek: WorkDay[]}) => {
        const {disabled, workWeek = INITIAL_WORK_WEEK} = payload ?? {}

        return (
            <Collapse className={styles.panel}>
                <Panel header="Define WorkDays & Time" key="1" className={styles.panel}>
                    <div className={disabled ? styles.workWeekName : styles.workweekContainer}>
                        {workWeek.map((item, index) => {
                            return (
                                <WorkDayItem
                                    key={index}
                                    enableMaxHours
                                    item={item}
                                    onChange={(_workDay) => {
                                        const _workWeek = [...workWeek]
                                        _workWeek[index] = _workDay
                                        onChangeNewItemData("workWeek", _workWeek)
                                    }}
                                />
                            )
                        })}
                    </div>
                </Panel>
            </Collapse>
        )
    }

    const renderNameInput = (payload: {value: string; workWeek?: any[]; disabled?: boolean}) => {
        const {value, workWeek, disabled} = payload

        return (
            <div className={styles.inputWrap}>
                <BaseInput
                    value={value}
                    placeholder="Name"
                    onChange={(value) => onChangeNewItemData("nameData", value)}
                />
                {renderWorkWeek({
                    workWeek,
                    disabled
                })}
            </div>
        )
    }

    const renderTimeInput = (payload: {key: "startTime" | "endTime"; startTime: string; endTime: string}) => {
        const {key} = payload
        const todayStr = moment().format("YYYY-MM-DD")
        const startTime = payload["startTime"] ? moment(todayStr + " " + payload["startTime"]) : undefined
        const endTime = payload["endTime"] ? moment(todayStr + " " + payload["endTime"]) : undefined
        const format = model.getUserTimeFormat()

        return (
            <BaseTimePicker
                key={key}
                value={key === "startTime" ? startTime : endTime}
                onChange={(newValue) => {
                    const data = newValue ? moment(newValue).format(format) : null
                    const value = newValue ? moment(newValue) : null

                    if (
                        value &&
                        ((key === "startTime" && endTime && value.isAfter(endTime)) ||
                            (key === "endTime" && startTime && startTime.isAfter(value)))
                    ) {
                        onChangeNewItemData(key === "startTime" ? "endTime" : "startTime", data)
                    }

                    onChangeNewItemData(key, data)
                }}
                disabledHours={key === "endTime" ? () => getDisabledEndHours(startTime) : undefined}
                disabledMinutes={key === "endTime" ? () => getDisabledEndMinutes(startTime, endTime) : undefined}
                placeholder={key === "startTime" ? "From" : "To"}
                format={format}
                className={styles.timePicker}
                size="small"
            />
        )
    }

    const renderCampusesInput = (payload: {campuses: any[]; programs: any[]; disabled?: boolean}) => {
        const {campuses, programs, disabled} = payload

        return (
            <div className={styles.inputWrap}>
                <div style={{width: "250px"}}>
                    <CampusSelect
                        isDisabled={!!disabled}
                        displayOnlyActiveCampuses
                        className={styles.select}
                        isClearable
                        isMulti
                        value={campuses}
                        onChange={(data) => {
                            const campusIds = data?.map((item) => item.id)
                            const newPrograms = programs?.filter((item) => {
                                return item.campusIds?.some((campusId) => campusIds?.includes(campusId))
                            })
                            onChangeNewItemData("programs", newPrograms)
                            onChangeNewItemData("campuses", data)
                        }}
                    />
                </div>
            </div>
        )
    }

    const renderProgramsInput = (payload: {programs: any[]; campuses: any[]; disabled?: boolean}) => {
        const {programs, campuses, disabled} = payload
        const campusIds = campuses?.map((item) => item.id)

        return (
            <div className={styles.inputWrap}>
                <div style={{width: "250px"}}>
                    <ProgramSelect
                        isDisabled={!!disabled}
                        className={styles.select}
                        isClearable
                        isActive
                        campusId={campusIds}
                        isMulti
                        value={programs}
                        onChange={(data) => {
                            onChangeNewItemData("programs", data)
                        }}
                    />
                </div>
            </div>
        )
    }

    const renderProgramVersionsInput = (payload: {programs: any[]; programVersions: any[]; disabled?: boolean}) => {
        const {programs, programVersions, disabled} = payload
        const programIds = programs?.map((item) => item.id)

        return (
            <div className={styles.inputWrap}>
                <div style={{width: "250px"}}>
                    <ProgramVersionSelect
                        isDisabled={!!disabled}
                        className={styles.select}
                        isClearable
                        isActive
                        programId={programIds}
                        isMulti
                        value={programVersions}
                        onChange={(data) => {
                            onChangeNewItemData("programVersions", data)
                        }}
                    />
                </div>
            </div>
        )
    }

    const onClickAddItem = () => {
        const newItem = {
            id: uuid(),
            nameHtml: renderNameInput({value: ""}),
            startTimeHtml: renderTimeInput({key: "startTime", startTime: undefined, endTime: undefined}),
            endTimeHtml: renderTimeInput({key: "endTime", startTime: undefined, endTime: undefined}),
            isActive: false,
            isActiveHtml: renderIsActiveHtml(false),
            isAutoAssignShiftHtml: renderIsAutoAssignShift({
                value: false,
                disabled: false
            }),
            campusesHtml: renderCampusesInput({campuses: [], programs: [], disabled: false}),
            workWeek: INITIAL_WORK_WEEK,
            programsHtml: renderProgramsInput({programs: [], campuses: [], disabled: false}),
            programVersionsHtml: renderProgramVersionsInput({programs: [], programVersions: [], disabled: false}),
            isForm: true
        }
        const data = getCurrentData()
        data.push(newItem)
        setNewData(newItem)
        currentNewData.current = cloneDeep(newItem)
        setOldData(null)
        currentOldData.current = null
        dispatch({data: cloneDeep(data)})
    }

    const validateDataBeforeSubmit = () => {
        const {isActive, nameData} = newData

        let error: string

        if (!nameData) {
            error = t("common:validation.cantEmpty", {
                field: t("settings.name")
            })
        }

        if (!isBoolean(isActive)) {
            error = t("common:validation.cantEmpty", {
                field: t("settings.isActive")
            })
        }

        if (error) {
            toastError(error)
            return false
        }
        return true
    }

    const onClickSave = async () => {
        if (!validateDataBeforeSubmit()) {
            return
        }

        const {
            nameData,
            startTime,
            endTime,
            isActive,
            isAutoAssignShift,
            isForm,
            id,
            programs,
            programVersions,
            workWeek,
            campuses
        } = newData

        const workdays = workWeek.filter((x) => x.enabled === true)

        if (isForm) {
            const submitData = {
                name: nameData,
                startTime: startTime || undefined,
                endTime: endTime || undefined,
                isActive,
                isAutoAssignShift,
                programIds: programs?.map((p) => p.id) || [],
                campusIds: campuses?.map((c) => c.id) || [],
                programVersionIds: programVersions?.map((p) => p.id) || [],
                workWeek: []
            }
            submitData.workWeek = workdays.map((x) => ({
                dayOfWeek: x.dayOfWeek,
                maxHoursEnabled: !!x.maxHoursEnabled,
                maxHours: x.maxHours?.length ? parseFloat(x.maxHours) : undefined,
                allDay: x.allDay,
                startTime: x.startTime?.format("HH:mm"),
                endTime: x.endTime?.format("HH:mm")
            }))

            createSetting(submitData)
        } else {
            const submitData = {
                name: nameData,
                startTime: startTime || undefined,
                endTime: endTime || undefined,
                isActive,
                isAutoAssignShift,
                programIds: programs?.map((p) => p.id) || [],
                campusIds: campuses?.map((c) => c.id) || [],
                programVersionIds: programVersions?.map((p) => p.id) || [],
                workWeek: []
            }
            submitData.workWeek = workdays.map((x) => ({
                dayOfWeek: x.dayOfWeek,
                allDay: x.allDay,
                maxHoursEnabled: !!x.maxHoursEnabled,
                maxHours: x.maxHours?.length ? parseFloat(x.maxHours) : undefined,
                startTime: x.startTime?.format("HH:mm"),
                endTime: x.endTime?.format("HH:mm")
            }))
            updateSetting(id, submitData)
        }
    }

    const createSetting = async (data) => {
        createOrEditShiftMutation.mutate({
            isEdit: false,
            data
        })
    }

    const updateSetting = async (shiftId, data) => {
        createOrEditShiftMutation.mutate({
            shiftId,
            isEdit: true,
            data
        })
    }

    const onClickCancel = () => {
        const oldItemData = oldData
        const data = getCurrentData()
        const newData = data
            .filter((item) => !item.isForm)
            .map((item) => {
                if (oldItemData && oldItemData.id === item.id) {
                    item.nameHtml = renderName(oldItemData.name, oldItemData.workWeek)
                    item.startTimeHtml = oldItemData.startTime
                    item.endTimeHtml = oldItemData.endTime
                    item.isActive = oldItemData.isActive
                    item.isAutoAssignShift = oldItemData.isAutoAssignShift
                    item.isActiveHtml = renderIsActiveHtml(oldItemData.isActive, true)
                    item.isAutoAssignShiftHtml = renderIsAutoAssignShift({
                        value: oldItemData.isAutoAssignShift,
                        disabled: true
                    })
                    item.programsHtml = renderProgramsInput({
                        programs: oldItemData.programs,
                        campuses: oldItemData.campuses,
                        disabled: true
                    })
                    item.programVersionsHtml = renderProgramVersionsInput({
                        programs: oldItemData.programs,
                        programVersions: oldItemData.programVersions,
                        disabled: true
                    })
                    item.campusesHtml = renderCampusesInput({
                        campuses: oldItemData.campuses,
                        programs: oldItemData.programs,
                        disabled: true
                    })
                    return item
                }
                return item
            })
        setNewData(null)
        currentNewData.current = null
        setOldData(null)
        currentOldData.current = null
        dispatch({data: newData})
    }

    const onConfirmManualAssignShift = () => {
        if (currentShiftSelected) {
            manualAssignShiftMutation.mutate({
                shiftIds: [currentShiftSelected.shiftId],
                campusIds: currentShiftSelected.campusIds,
                majorIds: currentShiftSelected.majorIds,
                majorVersionIds: currentShiftSelected.majorVersionIds
            })
        }
    }

    const onCancelManualAssignShift = () => {
        popupConfirmManualAssignShift.close()
    }

    return (
        <div className={styles.wrapper}>
            <KlassappTableHeader
                isShowAction={isShowTableHeaderAction}
                actions={tableHeaderActions}
                page={page}
                total={total}
                defaultPageSize={pageSize}
                onChangePage={onChangePage}
                onChangeRowPerPage={onChangeRowPerPage}
                fields={fields}
                allFields={allFields}
                onChangeFields={onChangeFields}
                onChangeAllFields={onChangeAllFields}
                onDraggableColumn={onDraggableColumn}
            />
            <KlassappTable
                columns={columns}
                data={data}
                menuActions={isHideMenuActions ? [] : menuActions}
                isLoading={isLoading}
                fields={fields}
                allFields={allFields}
                isShowCheckedColumn
                tableClassName={styles.studentTable}
                onChangeFields={onChangeFields}
                onUpdateRowData={onUpdateRowData}
                onUpdateTableData={onUpdateTableData}
                onClickSortColumn={onClickSortColumn}
                onDraggableColumn={onDraggableColumn}
                onChangeAllFields={onChangeAllFields}
            />
            {!newData ? (
                !isLoading && <AddItemCircleButton onClick={onClickAddItem} />
            ) : (
                <div className={styles.buttonWrap}>
                    <SecondaryButton
                        title={t("common:action.cancel")}
                        onClick={onClickCancel}
                        className={styles.cancelBtn}
                    />
                    <BaseButton
                        title={t("common:action.save").toUpperCase()}
                        onClick={onClickSave}
                        loading={isLoading}
                    />
                </div>
            )}
            <ConfirmPopup
                isVisible={popupConfirmManualAssignShift.isVisible}
                title={"Are you sure?"}
                loading={manualAssignShiftMutation.isLoading}
                onClose={onCancelManualAssignShift}
                onConfirm={onConfirmManualAssignShift}
            />
        </div>
    )
}

export const Shift = KlassappTableHOC(ShiftTab)
